import { useMsal } from '@azure/msal-react';
import logoutIcon from '@src/main/assets/icons/logout.svg';
import logoFull from '@src/main/assets/images/iberia-logo.svg';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import styles from '@src/main/shared/ui/components/navbar/Navbar.module.scss';
import { Avatar } from 'primereact/avatar';
import { Menubar } from 'primereact/menubar';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

const Navbar: FC = () => {
  const [items, setItems] = useState<MenuItem[]>([]);
  const { user, hasPermission } = useAuth();

  interface MenuItem {
    label: string;
    icon?: string;
    url?: string;
    command?: () => void;
    items?: MenuItem[];
  }

  const { t } = useTranslation();
  const { instance } = useMsal();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    instance.logoutRedirect({
      postLogoutRedirectUri: '/login',
    });
  };

  useEffect(() => {
    const reportsItems: MenuItem[] = [];

    if (hasPermission(PERMISSIONS.REPORTS.view)) {
      reportsItems.push({
        label: t('reports.menu.punctuality'),
        command: () => navigate('/reports'),
      });
    }

    if (hasPermission(PERMISSIONS.EVALUATIONS.view)) {
      reportsItems.push({
        label: t('reports.menu.evaluations'),
        command: () => navigate('/reports/evaluations'),
      });
    }
    if (hasPermission(PERMISSIONS.EVALUATIONS.view)) {
      reportsItems.push({
        label: t('reports.menu.positionassignment'),
        command: () => navigate('/reports/positionassignment'),
      });
    }

    const menuItems: MenuItem[] = [
      {
        label: t('parameters.title'),
        items: [
          {
            label: t('parameters.fleetsAndPositions.title'),
            command: () => navigate('/parameters/positions_fleets'),
          },
          {
            label: t('parameters.fleetAssignments.title'),
            command: () => navigate('/parameters/fleet_assignment'),
          },
          {
            label: t('parameters.parametersManagement.title'),
            command: () => navigate('/parameters/manage_parameters'),
          },
          {
            label: t('parameters.releaseNotes.title'),
            command: () => navigate('/parameters/release_notes'),
          },
          {
            label: t('parameters.issues.title'),
            command: () => navigate('/parameters/issues'),
          },
        ],
      },
      {
        label: t('messages.title'),
        items: [
          {
            label: t('messages.mailbox.title'),
            command: () => navigate('/messages/mailbox'),
          },
          {
            label: t('messages.subjects.title'),
            command: () => navigate('/messages/subjects'),
          },
          {
            label: t('messages.addresses.title'),
            command: () => navigate('/messages/addresses'),
          },
        ],
      },
      {
        label: t('usersManagement.title'),
        command: () => navigate('/users-management'),
      },
      {
        label: t('rolesManagement.title'),
        command: () => navigate('/roles-management'),
      },
    ];

    if (reportsItems.length > 0) {
      menuItems.push({
        label: t('reports.title'),
        items: reportsItems,
      });
    }

    setItems(menuItems);
  }, [hasPermission, navigate, t]);

  return (
    <Menubar
      model={items}
      className={styles['custom-menubar']}
      pt={{
        menuitem: { className: styles['custom-menu-item'] },
        label: { className: styles['custom-menu-item-label'] },
        root: { className: styles['custom-menubar-root'] },
        menu: { className: styles['custom-menubar-menu'] },
        submenu: { className: styles['custom-submenu'] },
      }}
      start={
        <div className={`${styles['navbar-logo']} ${styles['navbar-logo-small']} `}>
          <Link to="/">
            <img className={styles['text-logo']} src={logoFull} alt="Iberia title and logo" />
            <img className={styles['only-logo']} src={logoFull} alt="Iberia logo" />
          </Link>
        </div>
      }
      end={
        <div className={styles['navbar-user']}>
          <span className={styles['navbar-username']}>
            {user?.fullName} ({user?.employeeId})
          </span>
          <Avatar
            className={styles['navbar-avatar']}
            image={logoutIcon}
            shape="circle"
            size="normal"
            onClick={handleLogout}
          />
        </div>
      }
      menuIcon="pi pi-bars"
    />
  );
};

export default Navbar;
